import Swal from "sweetalert2";
import UtilService from "./UtilService";
import ReactDOMServer from "react-dom/server";
import TermsOfUseContent from "../data/TermsOfUseContent";
import SignaturePad from 'signature_pad';
import React, { useEffect, useState, type FC } from "react";
import { Form } from "react-bootstrap";
import { toast, Slide } from "react-toastify";
import type { FullAddressInterface } from "../interfaces/FullAddressInterface";
import { createRoot } from "react-dom/client";
import WriteWithAI from "../Admin/Components/WriteWithAI/WriteWithAi";
import Decimal from "decimal.js";

/**
 * ServiceOrderServiceTotals
 * 
 * This class provides comprehensive financial calculations for Service Orders, including
 * support for multiple markup types and complex pricing scenarios.
 * 
 * ====================================================================================
 * MARKUP TYPES - Pricing Strategy Documentation
 * ====================================================================================
 * 
 * The system supports 4 distinct markup types, each serving different business needs:
 * 
 * 1. 'percentage' - Percentage of Cost Markup
 *    • Formula: baseCost × (markup ÷ 100)
 *    • Use: Standard profit margins (e.g., 25% markup)
 *    • Example: $100 base cost + 25% = $125 total
 *    • Fields used: markup (percentage value)
 * 
 * 2. 'value' - Add Flat Dollar Amount  
 *    • Formula: baseCost + markupValue
 *    • Use: Fixed fees, handling charges
 *    • Example: $100 base cost + $20 fee = $120 total
 *    • Fields used: markupValue (dollar amount)
 * 
 * 3. 'perUnit' - Add Dollar Amount Per Unit
 *    • Formula: baseCost + (markupValue × quantity)
 *    • Use: Per-item processing fees, handling charges
 *    • Example: $100 base + ($3 × 10 units) = $130 total
 *    • Fields used: markupValue (per-unit amount), quantity
 * 
 * 4. 'costPrice' - Enter Price Independently (Price Override)
 *    • Formula: markupValue (ignores base cost entirely)
 *    • Use: Retail pricing, competitive pricing, quoted prices
 *    • Example: $100 base cost → $80 final price (price override)
 *    • Fields used: markupValue (total final price)
 *    • Technical: Returns (markupValue - baseCost) so baseCost + markup = markupValue
 * 
 * ====================================================================================
 * CALCULATION SCENARIOS
 * ====================================================================================
 * 
 * Kitchen Renovation Example:
 * • Cabinets: $5,000 base × 25% = $6,250 (percentage)
 * • Labor: $2,000 base + $300 = $2,300 (value)  
 * • Hardware: $375 base + ($5 × 25 units) = $500 (perUnit)
 * • Countertops: $2,400 base → $3,200 final (costPrice override)
 * Total: $12,250
 * 
 * Emergency Service Example:
 * • Materials: $150 base × 40% = $210 (percentage)
 * • Call Fee: $0 base + $75 = $75 (value)
 * • Fittings: $96 base + ($3 × 12 units) = $132 (perUnit)  
 * • Equipment: $400 base → $650 retail (costPrice override)
 * Total: $1,067
 * 
 * ====================================================================================
 * FINANCIAL METRICS DOCUMENTATION
 * ====================================================================================
 * 
 * The following methods calculate various financial aspects of service orders:
 * 
 * • grossContractTotal: Total before discounts (items + approved change orders)
 * • discountApplied: Sum of all applied discounts
 * • netContractTotal: Final contract value (gross - discounts)
 * • builderCost: Direct costs only (no markup/profit)
 * • profitAmount: Total markup profit from original items
 * • changeOrder: Direct cost of approved change order items
 * • profitChangeOrder: Markup profit from change orders
 * • jobRunning: Current job scope (net + open change orders)
 * • openInvoice: Outstanding unpaid invoice amounts
 * • amountPaid: Total payments received
 * • remainingBalance: Outstanding balance to be paid
 * 
 * ====================================================================================
 */

const UtilServiceReact = () => {

    const alertClassesOld = {
        customClass: {
            container: 'swal-custom-container',
            popup: 'swal-custom-popup',
            input: 'form-control'
        },
        showClass: {
            popup: 'my-custom-show-animation',
        },
        hideClass: {
            popup: 'my-custom-hide-animation',
        },
    };

    const alertClasses = {
        customClass: {
            container: 'swal-custom-container',
            popup: 'swal-custom-popup',
            // Add specific classes for better targeting
            htmlContainer: 'swal-html-container-custom',
            input: 'form-control swal-input-custom', // Add custom input class
            // Add a class for the container Swal puts the input in
            inputContainer: 'swal-input-container-custom'
        },
        showClass: {
            popup: 'my-custom-show-animation',
        },
        hideClass: {
            popup: 'my-custom-hide-animation',
        },
    };

    const showWarningAlert = (title: string, description: string) => {
        return Swal.fire({
            title: `${title}`,
            text: `${description}`,
            icon: 'warning',
            confirmButtonText: "OK",
            cancelButtonText: "CANCEL",
            showCancelButton: true,
            confirmButtonColor: "#04dd70",
            cancelButtonColor: "#f77369",
            ...alertClasses
        });
    }

    const showSuccessAlert = (title: string, description: string) => {
        return Swal.fire({
            title: `${title}`,
            text: `${description}`,
            icon: "success",
            confirmButtonText: "Confirm",
            cancelButtonText: "CANCEL",
            showCancelButton: true,
            confirmButtonColor: "#04dd70",
            cancelButtonColor: "#f77369",
            ...alertClasses
        });
    }

    const showTitleAlert = (title: string, description: string, duration?: number) => {
        return Swal.fire({
            title: title,
            text: description,
            confirmButtonColor: "#04dd70",
            showCancelButton: false,
            showConfirmButton: false,
            showCloseButton: false,
            allowOutsideClick: false,
            timer: duration ?? 4000,
            timerProgressBar: true,
            ...alertClasses
        })
    }

    const showTextConfirmationAlert = (title: any, description: string, key: string, showSignatureCanva = false) => {
        return Swal.fire({
            title: `${title}`,
            html: `${description}`,
            icon: 'warning',
            input: "text",
            inputAttributes: {
                autocapitalize: "true",
            },
            showCancelButton: true,
            confirmButtonText: "Confirm",
            confirmButtonColor: "#04dd70",
            cancelButtonColor: "#f77369",
            showLoaderOnConfirm: true,
            preConfirm: (valueTyped) => {
                const normalizedKey = key.trim().toLowerCase();
                const normalizedValueTyped = valueTyped?.trim().toLowerCase();

                if (normalizedValueTyped !== normalizedKey) {
                    Swal.showValidationMessage("Typed value does not match the expected key: " + key);
                    return;
                }

                if (showSignatureCanva) {
                    return true;
                }
            },
            allowOutsideClick: () => !Swal.isLoading(),
            ...alertClasses
        });
    };

    const showTextConfirmationAlertInputText = (title: any, description: string) => {
        return Swal.fire({
            title: `${title}`,
            html: `${description}`,
            icon: 'warning',
            input: "text",
            inputAttributes: {
                autocapitalize: "true",
            },
            showCancelButton: true,
            confirmButtonText: "Confirm",
            confirmButtonColor: "#04dd70",
            cancelButtonColor: "#f77369",
            showLoaderOnConfirm: true,
            preConfirm: (valueTyped) => {
                if (valueTyped?.trim() === "") {
                    Swal.showValidationMessage("The input cannot be empty.");
                    return null;
                }
                return valueTyped;
            },
            allowOutsideClick: () => !Swal.isLoading(),
            ...alertClasses
        });
    };

    const showSignaturePad = () => {
        return Swal.fire({
            title: 'Digital Signature',
            html: `
                <h5>Draw your personal signature and confirm<h5>
                <div class="m-auto text-center">
                    <canvas id="signature-pad" class="signature-pad" width="350" height="150" style="border: 1px solid; border-radius: 15px;"></canvas>
                    <div class="btn-group w-100" role="group">
                    <button id="eraseButton" class="btn btn-outline-pronto" style="margin-top: 10px;">Clear</button>
                    <button id="saveJPGButton" class="btn btn-outline-pronto" style="margin-top: 10px;" data-action="save-jpg">Confirm signature</button>
                    </div>
                </div>
                <input type="hidden" id="hfSign" />`,
            focusConfirm: false,
            showConfirmButton: false,
            showCancelButton: true,
            cancelButtonText: 'Close',
            confirmButtonColor: "#04dd70",
            cancelButtonColor: "#f77369",
            icon: 'warning',
            didOpen: () => {
                const canvas = document.getElementById('signature-pad') as HTMLCanvasElement;
                const signaturePad = new SignaturePad(canvas, {
                    backgroundColor: 'rgb(255, 255, 255)'
                });

                (canvas as any).signaturePad = signaturePad;

                document.getElementById('eraseButton')?.addEventListener('click', (e) => {
                    signaturePad.clear();
                    e.preventDefault();
                });

                document.getElementById('saveJPGButton')?.addEventListener('click', function () {
                    if (signaturePad.isEmpty()) {
                        Swal.showValidationMessage("Please provide a signature.");
                    } else {
                        var dataURL = signaturePad.toDataURL("image/jpeg");
                        var blob = dataURLToBlob(dataURL);
                        Swal.close({ isConfirmed: true, value: blob });
                    }
                });
            },
            ...alertClasses
        });
    };

    function dataURLToBlob(dataURL: any) {
        var parts = dataURL.split(';base64,');
        var contentType = parts[0].split(":")[1];
        var raw = window.atob(parts[1]);
        var rawLength = raw.length;
        var uInt8Array = new Uint8Array(rawLength);

        for (var i = 0; i < rawLength; ++i) {
            uInt8Array[i] = raw.charCodeAt(i);
        }

        return new Blob([uInt8Array], { type: contentType });
    }

    function download(dataURL: any, filename: any) {
        var blob = dataURLToBlob(dataURL);
        var url = window.URL.createObjectURL(blob);
        var a: any = document.createElement("a");
        a.style = "display: none";
        a.href = url;
        a.download = filename;

        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(url);
    }

    const showTextSimpleInputWithAI = (
        title: string,
        description: string,
        validationErrorMsg: string,
        inputValue: string, // Initial value
        inputType: 'text' | 'textarea' = 'text',
        fieldName?: string,
        aiContext?: string
    ) => {
        let aiButtonRoot: any = null;
        // No need for inputId if using Swal.getInput()

        return Swal.fire({
            title: `${title}`,
            html: `
                <div style="text-align: left; margin-bottom: 10px; font-size: 11px">${description}</div>
                <!-- Placeholder for the AI button -->
                <div id="swal-ai-button-placeholder"></div>
            `,
            icon: 'warning',
            input: inputType,
            inputAttributes: {
                autocapitalize: "true",
                // id: inputId // ID might not be strictly needed anymore
            },
            showCancelButton: true,
            confirmButtonText: "Confirm",
            confirmButtonColor: "#04dd70",
            cancelButtonColor: "#f77369",
            showLoaderOnConfirm: true,
            inputValue: inputValue, // Set the initial value
            didOpen: (popup) => {
                const inputContainer = popup.querySelector('.swal2-input-container') as HTMLElement;
                const placeholder = popup.querySelector('#swal-ai-button-placeholder') as HTMLElement;

                if (inputContainer) {
                    inputContainer.style.position = 'relative'; // For AI button positioning
                }

                if (!fieldName || !placeholder) return;

                if (inputContainer) {
                    inputContainer.appendChild(placeholder); // Move placeholder for positioning
                }

                // *** Function to update the Swal input using Swal.getInput() ***
                const handleAIUpdate = (name: string, newValue: string) => {
                    const swalInput = Swal.getInput() as HTMLInputElement | HTMLTextAreaElement | null; // Use Swal's method

                    if (swalInput) {
                        swalInput.value = newValue; // Set the value

                        // Dispatch events to ensure UI updates and potential listeners react
                        const inputEvent = new Event('input', { bubbles: true, cancelable: true });
                        swalInput.dispatchEvent(inputEvent);
                        const changeEvent = new Event('change', { bubbles: true, cancelable: true });
                        swalInput.dispatchEvent(changeEvent);

                    } else {
                        console.error(`Could not find Swal input element to update.`);
                    }
                };

                // *** MODIFIED GETTER FUNCTION using Swal.getInput() ***
                const getCurrentSwalInputValue = () => {
                    const swalInput = Swal.getInput(); // Use Swal's dedicated method
                    // Ensure it's the correct type before accessing value
                    return swalInput ? (swalInput as HTMLInputElement | HTMLTextAreaElement).value : '';
                };

                // Create root and render WriteWithAI
                aiButtonRoot = createRoot(placeholder);
                aiButtonRoot.render(
                    <React.StrictMode>
                        <WriteWithAI
                            fieldName={fieldName}
                            getInputCurrentValue={getCurrentSwalInputValue} // Pass the modified getter
                            context={aiContext || `Editing field: ${fieldName}`}
                            onUpdate={handleAIUpdate}
                            variant="link"
                            size="sm"
                            className="p-0 swal-ai-button" // Ensure CSS positions this correctly
                            label={<>✨ Write with AI</>}
                        />
                    </React.StrictMode>
                );
            },
            willClose: () => {
                // Cleanup React root
                if (aiButtonRoot) {
                    aiButtonRoot.unmount();
                    aiButtonRoot = null;
                }
            },
            // *** MODIFIED preConfirm using Swal.getInput() ***
            preConfirm: () => {
                const swalInput = Swal.getInput() as HTMLInputElement | HTMLTextAreaElement | null;
                const currentValue = swalInput ? swalInput.value : ''; // Get value directly

                if (currentValue && currentValue.trim() !== '') {
                    return currentValue.trim(); // Return the confirmed value
                } else {
                    Swal.showValidationMessage(validationErrorMsg);
                    return false; // Prevent closing if validation fails
                }
            },
            allowOutsideClick: () => !Swal.isLoading(),
            ...alertClasses
        });
    }

    const showTextSimpleInput = (title: string, description: string, validationErrorMsg: string, inputValue: string, inputType: any = 'text') => {
        return Swal.fire({
            title: `${title}`,
            html: `${description}`,
            icon: 'warning',
            input: inputType,
            inputAttributes: {
                autocapitalize: "true",
            },
            showCancelButton: true,
            confirmButtonText: "Confirm",
            confirmButtonColor: "#04dd70",
            cancelButtonColor: "#f77369",
            showLoaderOnConfirm: true,
            inputValue: inputValue,
            preConfirm: (valueTyped: string) => {
                if (valueTyped) {
                    return valueTyped?.trim();
                } else {
                    Swal.showValidationMessage(validationErrorMsg);
                }
            },
            allowOutsideClick: () => !Swal.isLoading(),
            ...alertClasses
        })
    }

    const showSuccessTimerAlert = (title: string, description: string, duration?: number) => {
        return Swal.fire({
            title: title,
            text: description,
            icon: "success",
            confirmButtonColor: "#04dd70",
            confirmButtonText: "Got it",
            allowOutsideClick: false,
            timer: duration ?? 1000,
            timerProgressBar: true,
            ...alertClasses
        })
    }

    const showSuccessSimpleToast = (title: string) =>
        toast.success(
            <p className="text-white tx-16 mb-0 ">{title}</p>,
            {
                position: toast.POSITION.TOP_RIGHT,
                hideProgressBar: false,
                transition: Slide,
                autoClose: 5000,
                theme: "colored",
            }
        );

    const showWarningSimpleToast = (title: string) =>
        toast.warning(
            <p className="text-white tx-16 mb-0 ">{title}</p>,
            {
                position: toast.POSITION.TOP_RIGHT,
                hideProgressBar: false,
                transition: Slide,
                autoClose: 5000,
                theme: "colored",
            }
        );

    const showErrorSimpleToast = (title: string) =>
        toast.error(
            <p className="text-white tx-16 mb-0 ">{title}</p>,
            {
                position: toast.POSITION.TOP_RIGHT,
                hideProgressBar: false,
                transition: Slide,
                autoClose: 5000,
                theme: "colored",
            }
        );

    const showWarningTimerAlert = (title: string, description: string, duration?: number) => {
        return Swal.fire({
            title: title,
            text: description,
            icon: "warning",
            confirmButtonColor: "#04dd70",
            confirmButtonText: "Got it",
            allowOutsideClick: false,
            timer: duration ?? 1000,
            timerProgressBar: true,
            ...alertClasses
        })
    }

    const emailValidations = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        let message: string;
        if (!emailRegex.test(email)) {
            message = 'Required. Please enter a valid email address.';
            return { msg: message, isValid: false }
        } else {
            return { msg: '', isValid: true }
        }
    };

    const phoneValidations = (phone: string) => {
        if (phone) {
            let message: string;
            const phoneDigits = phone?.replace(/\D/g, ''); // Remove non-digit characters
            const phoneRegex = /^\d{9,11}$/;
            if (!phoneRegex.test(phoneDigits)) {
                message = 'Required. Please enter a valid 10-digit phone number.';
                return { msg: message, isValid: false }
            } else {
                return { msg: '', isValid: true }
            }
        } else {
            return { msg: "Required.", isValid: false }
        }
    }

    const requiredTextValidations = (text: string) => {
        let message: string;
        if (!text) {
            message = 'Required.';
            return { msg: message, isValid: false }
        }
        return { msg: '', isValid: true }
    };

    const validateEmailList = (emailNotifications: any[]) => {
        const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
        return (
            Array.isArray(emailNotifications) &&
            emailNotifications.length > 0 &&
            emailNotifications.every((email: string) => emailRegex.test(email))
        );
    };


    const requiredPositiveValueValidations = (value: number) => {
        let message: string;
        if (value <= 0) {
            message = 'Must be greater than 0.';
            return { msg: message, isValid: false }
        }
        return { msg: '', isValid: true }
    };

    const contractAmount = (items: any[], serviceOrder: any, applyDiscount: boolean = true) => {
        const baseCost = builderCost(items, serviceOrder, true, true);

        if (applyDiscount && serviceOrder?.discountApplied?.length > 0) {
            // Calculate proportional discount for these specific items
            const totalServiceOrderCost = builderCost(serviceOrder.items || [], serviceOrder, true, true);
            const totalDiscount = discountApplied(serviceOrder);

            if (totalServiceOrderCost > 0) {
                // Apply proportional discount based on item cost vs total service order cost
                const proportionalDiscount = (baseCost / totalServiceOrderCost) * totalDiscount;
                return baseCost - proportionalDiscount; // Allow negative values
            }
        }

        return baseCost;
    };

    // Calculate gross contract total (items + approved change orders with markup)
    const grossContractTotal = (items: any[], extraWorkOrders: any[], serviceOrder: any) => {
        const itemsTotal = builderCost(items, serviceOrder, true, true);
        const changeOrdersTotal = extraWorkOrders?.length > 0
            ? extraWorkOrders
                .filter((ewo: any) => ewo.status === 'APPROVED')
                .reduce((total: number, ewo: any) => {
                    return total + builderCost(ewo.items || [], serviceOrder, true, true);
                }, 0)
            : 0;
        return itemsTotal + changeOrdersTotal;
    };

    // Calculate discount applied
    const discountApplied = (serviceOrder: any) => {
        return serviceOrder?.discountApplied?.reduce((total: number, discount: any) => {
            return total + (discount.discountValue || 0);
        }, 0) || 0;
    };

    // Backend-style builderCost (only direct cost, no markup, no tax)
    const builderCostBackend = (items: any[]) => {
        return items?.reduce((accumulator: number, item: any) => {
            const baseCost = item.unitCost * item.quantity || 0;
            return accumulator + baseCost;
        }, 0) || 0;
    };

    // Calculate net contract total (matching backend logic)
    const netContractTotal = (items: any[], extraWorkOrders: any[], serviceOrder: any) => {
        const gross = grossContractTotal(items, extraWorkOrders, serviceOrder);
        const discount = discountApplied(serviceOrder);
        const builderCostTotal = builderCostBackend(items); // Only direct cost, no markup, no tax
        const profitAmountTotal = profitValue(items, serviceOrder, false); // Profit before discount

        const calculatedNet = gross - discount;
        const minimumNet = builderCostTotal + profitAmountTotal;

        return Math.max(calculatedNet, minimumNet);
    };

    // Calculate job running (matching backend logic)
    const jobRunning = (items: any[], extraWorkOrders: any[], serviceOrder: any) => {
        const net = netContractTotal(items, extraWorkOrders, serviceOrder);

        // Add open change orders (draft/pending) with full value including markup
        const openChangeOrdersTotal = extraWorkOrders?.length > 0
            ? extraWorkOrders
                .filter((ewo: any) => ['DRAFT', 'PENDING'].includes(ewo.status))
                .reduce((total: number, ewo: any) => {
                    return total + builderCost(ewo.items || [], serviceOrder, true, true);
                }, 0)
            : 0;

        return net + openChangeOrdersTotal;
    };

    const builderCost = (
        items: any[],
        serviceOrder: any,
        isCalcMarkup: boolean = true,
        isCalcTax: boolean = true
    ) => {

        const totalCost = items?.reduce((accumulator: number, item: any) => {
            // Backend logic: builderCost only includes direct cost (unitCost * quantity)
            // It does NOT include markup or tax
            const baseCost = item.unitCost * item.quantity || 0;

            // If we need to include markup and tax (for other calculations), add them
            let markupCost = 0;
            let taxCost = 0;

            if (isCalcMarkup) {
                markupCost = calcMarkup(baseCost, item, true) || 0;
            }

            if (isCalcTax && serviceOrder?.tax && item.applyTax) {
                taxCost = ((baseCost + markupCost) * (serviceOrder?.tax / 100)) || 0;
            }

            return accumulator + baseCost + markupCost + taxCost;
        }, 0) || 0;

        return totalCost;
    };

    const profitValue = (items: any[], serviceOrder: any, applyDiscount: boolean = true) => {
        // Backend logic: profit = markup - discount
        const totalMarkup = items?.reduce((accumulator: number, item: any) => {
            return accumulator + (item.markupValue || 0);
        }, 0) || 0;

        let profit = totalMarkup;

        if (applyDiscount) {
            const discount = discountApplied(serviceOrder);
            profit = profit - discount; // Allow negative values
        }



        return profit;
    };

    const calcMarkup = (baseCost: any, item: any, isCalcMarkup: boolean) => {
        if (!isCalcMarkup) {
            return 0; // Return 0 if markup calculation is disabled
        }
        let markupCost = 0;
        const markupType = item.markupType || 'value';
        switch (markupType) {
            case 'percentage':
                // markup is stored as whole number (e.g., 20 for 20%) after database migration
                markupCost = baseCost * ((typeof item.markup === 'number' ? item.markup : 0) / 100);
                break;
            case 'value':
                // Add flat dollar amount to base cost
                markupCost = typeof item.markupValue === 'number' ? item.markupValue : 0;
                break;
            case 'perUnit':
                // markupValue stores the total markup amount, not per-unit
                // For perUnit type, we need to calculate the per-unit amount from the total
                const totalMarkupAmount = typeof item.markupValue === 'number' ? item.markupValue : 0;
                const quantity = typeof item.quantity === 'number' ? item.quantity : 1;

                // Calculate per-unit amount from total markup
                const perUnitAmount = quantity > 0 ? totalMarkupAmount / quantity : 0;
                markupCost = totalMarkupAmount; // Use the total markup directly

                // Add validation for unreasonably high per-unit markup
                if (perUnitAmount > 1000) {
                    console.warn(`Warning: Unusually high per-unit markup detected: $${perUnitAmount} per unit for item "${item.name || 'Unknown'}"`);
                }
                break;
            case 'costPrice':
                // markupValue should be the final price, overriding baseCost entirely
                // So markupCost = finalPrice - baseCost to achieve: baseCost + markupCost = finalPrice
                const finalPrice = typeof item.markupValue === 'number' ? item.markupValue : 0;
                markupCost = finalPrice - baseCost;
                break;
            default:
                markupCost = 0;
        }
        return markupCost;
    };

    const priceItemString = (item: any, serviceOrder: any, calculateTax: boolean, isProvider: boolean = false) => {
        const unitCost = new Decimal(item.unitCost || 0);
        const quantity = new Decimal(item.quantity || 1);
        const builderCostForItem = unitCost.mul(quantity);
        const markupValue = new Decimal(item.markupValue || 0);
        const markupPercentage = new Decimal(item.markup || 0);
        const markupType = item.markupType || 'value'; // Default to 'value' if undefined

        let outputString = `Cost: ${UtilService.formatNumber(unitCost.toNumber())} x ${item.quantity}`;

        if (isProvider) {
            outputString += `<br>`; // Add line break for provider details
            switch (markupType) {
                case 'value':
                    outputString += `Markup: Flat <strong>${UtilService.formatNumber(markupValue.toNumber())}</strong> added.`;
                    if (builderCostForItem.gt(0) && !markupValue.isZero()) {
                        const equivalentPercentage = markupValue.div(builderCostForItem).mul(100);
                        outputString += ` (${UtilService.formatNumberWithoutCurrency(equivalentPercentage.toNumber())}% of cost)`;
                    }
                    break;
                case 'percentage':
                    // Calculate markup dollar amount dynamically for percentage-based markups
                    const calculatedMarkupAmount = builderCostForItem.mul(markupPercentage.div(100));
                    outputString += `Markup: <strong>${UtilService.formatNumberWithoutCurrency(markupPercentage.toNumber())}%</strong> of cost, adding <strong>${UtilService.formatNumber(calculatedMarkupAmount.toNumber())}</strong>.`;
                    break;
                case 'perUnit':
                    // markupValue stores the total markup amount, calculate per-unit amount
                    const totalMarkupAmount = markupValue;
                    const perUnitAmount = quantity.gt(0) ? totalMarkupAmount.div(quantity) : new Decimal(0);

                    // Add debugging for high markup values
                    if (perUnitAmount.toNumber() > 1000) {
                        console.warn(`High per-unit markup detected: $${perUnitAmount.toNumber()} per unit for item "${item.name || 'Unknown'}"`);
                    }

                    outputString += `Markup: <strong>${UtilService.formatNumber(perUnitAmount.toNumber())}</strong> per unit, totaling <strong>${UtilService.formatNumber(totalMarkupAmount.toNumber())}</strong>.`;
                    if (builderCostForItem.gt(0) && !totalMarkupAmount.isZero()) {
                        const equivalentPercentage = totalMarkupAmount.div(builderCostForItem).mul(100);
                        outputString += ` (${UtilService.formatNumberWithoutCurrency(equivalentPercentage.toNumber())}% of cost)`;
                    }
                    break;
                case 'costPrice':
                    // markupValue now stores the final price directly
                    outputString += `Markup: ${UtilService.formatNumber(markupValue.toNumber())}. Final price set to <strong>${UtilService.formatNumber(markupValue.toNumber())}</strong>.`;
                    break;
                default: // Fallback for unknown type or no markup
                    outputString += `Markup: ${UtilService.formatNumber(markupValue.toNumber())}`;
            }
        }

        return outputString;
    };

    const builderCostExtraWorkOrders = (extraWorkOrders: any[], serviceOrder: any) => {
        const extraWorkOrderItems = extraWorkOrders?.flatMap(extraWorkOrder => extraWorkOrder.items);
        return extraWorkOrderItems?.length > 0 ? builderCost(extraWorkOrderItems, serviceOrder, true) : 0;
    };



    const profitValueChangeOrder = (extraWorkOrders: any[], serviceOrder: any) => {
        if (extraWorkOrders?.length > 0) {
            const extraWorkOrderItems = extraWorkOrders?.flatMap(extraWorkOrder => extraWorkOrder?.items).filter((item: any) => item.status === 'APPROVED');
            // Backend logic: profit from change orders = markup - discount
            const totalMarkup = extraWorkOrderItems?.reduce((accumulator: number, item: any) => {
                return accumulator + (item.markupValue || 0);
            }, 0) || 0;

            const discount = discountApplied(serviceOrder);
            const profit = totalMarkup - discount; // Allow negative values

            return profit;
        } else {
            return 0;
        }
    }

    const priceItemTotalString = (item: any, serviceOrder: any) => {
        const totalCost = contractAmount([item], serviceOrder, false); // Use contractAmount to properly handle discounts
        return UtilService.formatNumber(totalCost); // Use formatNumber to match backend calculations
    };

    const priceItemOptionString = (item: any, serviceOrder: any) => {
        const totalCostWithMarkup = contractAmount([item], serviceOrder, true); // Use contractAmount to properly handle discounts
        const formattedTotalCost = UtilService.formatNumber(totalCostWithMarkup);
        const formattedUnitCost = UtilService.formatNumber(item.unitCost);
        return `Price: ${formattedUnitCost}<br>Qty: ${item.quantity}<br>Total: ${formattedTotalCost}`;
    };

    function calculateRemainingPayment(item: any, serviceOrder: any): number {
        let totalInvoiceValue = totalInvoicedPaidItems([item]) || 0;
        const totalItemCost = contractAmount([item], serviceOrder, true); // Use contractAmount which properly handles discounts
        const percentageInvoiced = totalItemCost > 0 ? (totalInvoiceValue / totalItemCost) * 100 : 0;
        const percentageRemaining = 100 - percentageInvoiced;
        return parseFloat((totalItemCost * (percentageRemaining / 100)).toFixed(2));
    }

    function calculatePercentageInvoiced(totalItem: number, item: any, serviceOrder: any): any {
        if (item?.invoiceHistory?.length === 0) {
            return 0;
        }
        if (totalItem === 0) {
            return 0;
        }
        const totalInvoicedValue = totalInvoicedPaidItems([item]);
        // totalItem should already be the net amount if calculated with contractAmount
        const percentage = (totalInvoicedValue / totalItem) * 100;
        return (percentage || 0);
    }

    const calculateTotalInvoiceValue = (invoiceHistory: any[]) => {
        return invoiceHistory?.reduce((accumulator, item) => accumulator + (item.invoiceValue || 0), 0);
    }

    const getItemsNotInvoicedYet = (items: any[], serviceOrder: any) => {
        const itemsNotInvoicedYet = items?.filter((item: any) => {
            const invoicedValue = calculateTotalInvoiceValue(item?.invoiceHistory) || 0;
            const itemContractAmount = contractAmount([item], serviceOrder, true); // Use contractAmount to properly consider discounts
            return invoicedValue < itemContractAmount;
        });
        return itemsNotInvoicedYet || [];
    }

    const getItemsNotInvoicedYetExtraWorkOrders = (extraWorkOrders: any[], serviceOrder: any) => {
        if (extraWorkOrders?.length > 0) {
            const extraWorkOrderItems = extraWorkOrders?.flatMap(extraWorkOrder => extraWorkOrder?.items);
            const itemsNotInvoicedYet = extraWorkOrderItems?.filter((item: any) => {
                const invoicedValue = calculateTotalInvoiceValue(item?.invoiceHistory) || 0;
                const itemContractAmount = contractAmount([item], serviceOrder, true); // Use contractAmount to properly consider discounts
                return invoicedValue < itemContractAmount;
            });
            return itemsNotInvoicedYet || [];
        } else {
            return [];
        }
    }

    const getTotalApprovedServiceOrders = (items: any[]) => {
        if (items?.length > 0) {
            const serviceOrderItems = items?.filter((item: any) => item.status === 'APPROVED');
            return serviceOrderItems?.length;
        } else {
            return 0;
        }
    }

    const getTotalApprovedExtraWorkOrders = (extraWorkOrders: any[]) => {
        if (extraWorkOrders?.length > 0) {
            const extraWorkOrderItems = extraWorkOrders?.filter((extraWorkOrder: any) => extraWorkOrder.status === 'APPROVED');
            return extraWorkOrderItems?.length;
        } else {
            return 0;
        }
    }


    const totalInvoicedItemsRef = (items: any[], ref: any, type: string) => {
        let totalInvoiced = 0;

        if (items?.length > 0) {

            const filteredItems = items.filter((historyItem: any) => {
                if (!historyItem?.invoiceRef) {
                    console.warn("Missing invoiceRef for item:", historyItem);
                }
                return historyItem?.invoiceRef && String(historyItem?.invoiceRef) === String(ref);
            });

            const total = filteredItems.reduce((accumulator, historyItem) => {
                const value = historyItem?.invoiceValue || 0;
                return accumulator + value;
            }, 0);

            totalInvoiced = total || 0;
        }

        return totalInvoiced;
    };

    const totalInvoiceValueOld = (items: any[], ref: any, type: string) => {
        const filteredItems = filterItemsByInvoiceRef(items, ref);
        return filteredItems[0]?.invoiceHistory[0]?.invoiceValue || 0;
    };

    const totalInvoiceValue = (items: any[], ref: any, type: string) => {
        if (!items?.length || !ref) {
            return 0;
        }

        let totalInvoiceValue = 0;

        // Handle both regular items and extra work order items
        if (type === 'ewo') {
            // For extra work orders, we need to flatten the items from all extra work orders
            const allExtraWorkOrderItems = items.flatMap(extraWorkOrder => extraWorkOrder?.items || []);

            totalInvoiceValue = allExtraWorkOrderItems.reduce((total: number, item: any) => {
                if (item?.invoiceHistory?.length) {
                    const matchingInvoices = item.invoiceHistory.filter((historyItem: any) =>
                        historyItem?.invoiceRef && String(historyItem.invoiceRef) === String(ref)
                    );

                    return total + matchingInvoices.reduce((sum: number, invoice: any) =>
                        sum + (invoice.invoiceValue || 0), 0
                    );
                }
                return total;
            }, 0);
        } else {
            // For regular service order items
            totalInvoiceValue = items.reduce((total: number, item: any) => {
                if (item?.invoiceHistory?.length) {
                    const matchingInvoices = item.invoiceHistory.filter((historyItem: any) =>
                        historyItem?.invoiceRef && String(historyItem.invoiceRef) === String(ref)
                    );

                    return total + matchingInvoices.reduce((sum: number, invoice: any) =>
                        sum + (invoice.invoiceValue || 0), 0
                    );
                }
                return total;
            }, 0);
        }

        return totalInvoiceValue;
    };

    const getInvoiceItems = (items: any[], ref: any, type: string) => {
        const filteredItems = filterItemsByInvoiceRef(items, ref);
        return filteredItems[0] || [];
    };

    const filterItemsByInvoiceRef = (items: any[], ref: any) => {
        if (!items?.length) {
            console.warn("No items provided or items array is empty.");
            return [];
        }

        // Filter items by checking invoiceHistory[].invoiceRef
        const filteredItems = items.filter(item => {
            if (!item?.invoiceHistory?.length) {
                console.warn("No invoiceHistory for item:", item);
                return false;
            }

            // Check if any invoiceHistory entry matches the ref
            const hasMatchingInvoiceRef = item?.invoiceHistory.some((historyItem: any) => {
                return historyItem?.invoiceRef && String(historyItem.invoiceRef) === String(ref);
            });

            return hasMatchingInvoiceRef;
        });

        return filteredItems;
    };

    const totalInvoicedNotPaidItems = (items: any[]) => {
        if (items) {
            const totalInvoiced = items
                .filter((item: any) => item?.invoiceHistory)
                .flatMap((item: any) => item?.invoiceHistory || [])
                .filter((historyItem: any) => !historyItem.isPaid)
                .reduce((accumulator, historyItem) => accumulator + (historyItem.invoiceValue || 0), 0);
            return totalInvoiced || 0;
        }
        return 0;
    }

    const totalInvoicedPaidItems = (items: any[]) => {
        if (items) {
            const totalPaidInvoices = items
                .filter((item: any) => item?.invoiceHistory)
                .flatMap((item: any) => item?.invoiceHistory || [])
                .filter((historyItem: any) => historyItem.isPaid)
                .reduce((accumulator, historyItem) => accumulator + (historyItem.invoiceValue || 0), 0);
            return totalPaidInvoices || 0;
        }
        return 0;

    }

    const totalInvoicedNotPaidItemsExtraWorkOrders = (extraWorkOrders: any[]) => {
        if (extraWorkOrders?.length > 0) {
            const extraWorkOrderItems = extraWorkOrders?.flatMap(extraWorkOrder => extraWorkOrder?.items);
            const totalPaidInvoices = extraWorkOrderItems
                .filter((item: any) => item?.invoiceHistory)
                .flatMap((item: any) => item?.invoiceHistory || [])
                .filter((historyItem: any) => !historyItem.isPaid)
                .reduce((accumulator, historyItem) => accumulator + (historyItem.invoiceValue || 0), 0);
            return totalPaidInvoices || 0;
        }
        return 0;
    }

    const totalInvoicedPaidItemsExtraWorkOrders = (extraWorkOrders: any[]) => {
        if (extraWorkOrders?.length > 0) {
            const extraWorkOrderItems = extraWorkOrders?.flatMap(extraWorkOrder => extraWorkOrder?.items);
            const totalPaidInvoices = extraWorkOrderItems
                .filter((item: any) => item?.invoiceHistory)
                .flatMap((item: any) => item?.invoiceHistory || [])
                .filter((historyItem: any) => historyItem.isPaid)
                .reduce((accumulator, historyItem) => accumulator + (historyItem.invoiceValue || 0), 0);
            return totalPaidInvoices || 0;
        }
        return 0;
    }

    /*
    const invoiceHistoryList = (items: any[]) => {
        if (items) {
            const serviceOrderHistoryList = items
            .filter((item: any) => item?.invoiceHistory)
            .map((item: any) => item?.invoiceHistory)
            .reduce((accumulator: any, historyArray: []) => accumulator.concat(historyArray), []);
            return serviceOrderHistoryList;
        }
        return [];
    }
    */

    const invoiceHistoryList = (items: any[]) => {
        if (items) {
            const serviceOrderHistoryList = items
                .filter((item: any) => item?.invoiceHistory)
                .map((item: any) => item?.invoiceHistory)
                .reduce((accumulator: any, historyArray: []) => accumulator.concat(historyArray), []);

            // Group by invoiceRef
            const groupedInvoiceHistory = serviceOrderHistoryList.reduce((result: any, invoiceItem: any) => {
                const invoiceRef = invoiceItem.invoiceRef;
                if (!result[invoiceRef]) {
                    result[invoiceRef] = [];
                }
                result[invoiceRef].push(invoiceItem);
                return result;
            }, {});

            // Transform into array with ref and items
            const invoiceHistoryArray = Object.keys(groupedInvoiceHistory).map((invoiceRef) => {
                return { ref: parseInt(invoiceRef), items: groupedInvoiceHistory[invoiceRef] };
            });

            return invoiceHistoryArray;
        }
        return [];
    };

    const invoiceHistoryListExtraWorkOrder = (extraWorkOrders: any[]) => {
        if (extraWorkOrders?.length > 0) {
            const extraWorkOrderItems = extraWorkOrders?.flatMap(extraWorkOrder => extraWorkOrder.items);
            const serviceOrderHistoryList = extraWorkOrderItems
                .filter((item: any) => item?.invoiceHistory)
                .map((item: any) => item?.invoiceHistory)
                .reduce((accumulator: any, historyArray: []) => accumulator.concat(historyArray), []);

            // Group by invoiceRef
            const groupedInvoiceHistory = serviceOrderHistoryList.reduce((result: any, invoiceItem: any) => {
                const invoiceRef = invoiceItem.invoiceRef;
                if (!result[invoiceRef]) {
                    result[invoiceRef] = [];
                }
                result[invoiceRef].push(invoiceItem);
                return result;
            }, {});

            // Transform into array with ref and items
            const invoiceHistoryArray = Object.keys(groupedInvoiceHistory).map((invoiceRef) => {
                return { ref: parseInt(invoiceRef), items: groupedInvoiceHistory[invoiceRef] };
            });

            return invoiceHistoryArray;
        }
        return [];
    };


    const changeOrderTotal = (extraWorkOrders: any[], serviceOrder: any) => {
        if (extraWorkOrders?.length > 0) {
            const extraWorkOrderItems = extraWorkOrders?.flatMap(extraWorkOrder => extraWorkOrder.items).filter((item: any) => item.status === 'APPROVED');
            return extraWorkOrderItems?.length > 0 ? builderCostBackend(extraWorkOrderItems) : 0;
        }
        return 0;
    }

    const calculateTaxValue = (items: any[], tax: number) => {
        const taxRate = tax > 0 ? new Decimal(tax).div(100) : new Decimal(0);
        const filteredItems = items?.filter(item => item.applyTax);
        return filteredItems?.reduce((acc, item) => {
            const costBeforeTax = new Decimal(builderCost([item], { tax: 0 }, true, false)); // Calculate cost with markup, without tax
            const itemTax = costBeforeTax.mul(taxRate);
            return acc.plus(itemTax);
        }, new Decimal(0)).toNumber() || 0;
    };

    const calculateTotals = (items: any[], serviceOrder: any, discount: number) => {
        const subtotalWithMarkup = builderCost(items, serviceOrder, true, false);
        const taxValue = calculateTaxValue(items, serviceOrder?.tax);
        const discountValue = discount || 0;
        const totalValue = subtotalWithMarkup + taxValue - discountValue;
        return {
            subtotalWithMarkup,
            taxValue,
            discountValue,
            totalValue
        };
    };

    const validatePositiveValue = (value: number) => {
        return value > 0 ? value : 0;
    }

    const validateEmptyText = (value: string) => {
        return UtilService.isEmpty(value) ? "" : value;
    }

    const numberToFixed2 = (value: number) => {
        return parseFloat(value.toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2 });
    }

    const termsOfUse = () => {
        const termsOfUseHtml = ReactDOMServer.renderToStaticMarkup(<TermsOfUseContent />);
        Swal.fire({
            title: 'Terms of Use',
            html: termsOfUseHtml,
            width: 600,
            padding: '3em',
            backdrop: `
            rgba(0,0,123,0.4)
            url("/images/nyan-cat.gif")
            left top
            no-repeat
            `,
            ...alertClasses
        });
    }

    const errorMessage: FC<{ error: string }> = ({ error }) => { // Correct destructuring
        const [showError, setShowError] = useState(!!error);

        useEffect(() => {
            let timeoutId: ReturnType<typeof setTimeout>; // More precise type for timeoutId

            if (showError) {
                timeoutId = setTimeout(() => {
                    setShowError(false);
                }, 5000);
            }

            return () => clearTimeout(timeoutId);
        }, [showError]);

        return showError ? (
            <Form.Text className='font-weight-semibold' style={{ color: "#ff0000" }}>
                {error}
            </Form.Text>
        ) : null;
    };

    const useDebounce = (value: any, delay: number) => {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            return () => {
                clearTimeout(handler);
            };
        }, [value, delay]);

        return debouncedValue;
    }

    const addressBlock = (address: string, fullAddress: FullAddressInterface) => {
        const {
            street = '',
            city = '',
            state = '',
            zip = '',
            country = '',
            addressDetails = ''
        } = fullAddress ?? {};

        const concatedAddress = street
            ? `${street}, ${city}, ${state}, ${zip}, ${country}, ${addressDetails}`
            : address;

        return <label>{concatedAddress}</label>;
    };

    // Landing Page Content for Pronto AI Agents and Voice Agents
    const landingPageContent = `
        <div class="pronto-ai-landing">
          <h2 class="text-center mb-4">Unlock the Power of AI with Pronto360</h2>
          <p class="lead text-center mb-5">
            Transform your business operations and customer interactions with our cutting-edge AI solutions.
          </p>
    
          <div class="row">
            <div class="col-md-6">
              <div class="mb-1">
                <div class="card-body">
                  <h3 class="card-title">Pronto AI Agents</h3>
                  <p class="card-text">
                    Our AI Agents are designed to automate tasks, enhance productivity, and provide intelligent insights.
                    They learn and adapt to your business needs, ensuring seamless integration and optimal performance.
                  </p>
                  <ul class="list-unstyled">
                    <li><i class="bi bi-check-circle-fill text-success me-2"></i> <strong>24/7 Availability:&nbsp;</strong> Agents work around the clock.</li>
                    <li><i class="bi bi-check-circle-fill text-success me-2"></i> <strong>Task Automation:&nbsp;</strong> Automate repetitive tasks.</li>
                    <li><i class="bi bi-check-circle-fill text-success me-2"></i> <strong>Data Analysis:&nbsp;</strong> Gain valuable insights from your data.</li>
                    <li><i class="bi bi-check-circle-fill text-success me-2"></i> <strong>Customization:&nbsp;</strong> Tailor agents to your specific needs.</li>
                  </ul>
                </div>
              </div>
            </div>
    
            <div class="col-md-6">
              <div class="mb-4">
                <div class="card-body">
                  <h3 class="card-title">Pronto AI Voice Assistant</h3>
                  <p class="card-text">
                    Revolutionize your customer service with our AI Voice Assistant. It provides natural, human-like
                    interactions, ensuring customer satisfaction and operational efficiency.
                  </p>
                  <ul class="list-unstyled">
                    <li><i class="bi bi-check-circle-fill text-success me-2"></i> <strong>Natural Language Processing:&nbsp;</strong> Understands and responds to human speech.</li>
                    <li><i class="bi bi-check-circle-fill text-success me-2"></i> <strong>Multilingual Support:&nbsp;</strong> Interact with customers in multiple languages.</li>
                    <li><i class="bi bi-check-circle-fill text-success me-2"></i> <strong>Personalized Interactions:&nbsp;</strong> Create unique customer experiences.</li>
                    <li><i class="bi bi-check-circle-fill text-success me-2"></i> <strong>Scalability:&nbsp;</strong> Handle a large volume of calls simultaneously.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
    
          <div class="text-center mt-5">
            <h4 class="mb-3">Why Choose Pronto AI?</h4>
            <p>
              Pronto AI is more than just technology; it's a strategic partner for your business growth.
              We offer solutions that are not only innovative but also practical and results-driven.
            </p>
            <p>
              <strong>Ready to transform your business?</strong> <a href="#">Contact us today</a> to learn more!
            </p>
          </div>
        </div>
      `;

    return {
        showWarningAlert,
        showWarningTimerAlert,
        showSuccessAlert,
        showSuccessTimerAlert,
        showSuccessSimpleToast,
        showWarningSimpleToast,
        showErrorSimpleToast,
        showTitleAlert,
        showTextConfirmationAlert,
        showTextSimpleInput,
        showTextSimpleInputWithAI,
        emailValidations,
        phoneValidations,
        requiredTextValidations,
        contractAmount,
        builderCost,
        builderCostExtraWorkOrders,
        profitValue,
        profitValueChangeOrder,
        validatePositiveValue,
        validateEmptyText,
        requiredPositiveValueValidations,
        changeOrderTotal,
        totalInvoicedItemsRef,
        totalInvoiceValue,
        getInvoiceItems,
        totalInvoicedNotPaidItems,
        totalInvoicedPaidItems,
        totalInvoicedNotPaidItemsExtraWorkOrders,
        totalInvoicedPaidItemsExtraWorkOrders,
        invoiceHistoryList,
        invoiceHistoryListExtraWorkOrder,
        calculateTotals,
        calculateTotalInvoiceValue,
        getItemsNotInvoicedYet,
        getItemsNotInvoicedYetExtraWorkOrders,
        priceItemString,
        priceItemTotalString,
        priceItemOptionString,
        termsOfUse,
        getTotalApprovedServiceOrders,
        getTotalApprovedExtraWorkOrders,
        showSignaturePad,
        errorMessage,
        showTextConfirmationAlertInputText,
        useDebounce,
        validateEmailList,
        addressBlock,
        calculateRemainingPayment,
        calculatePercentageInvoiced,
        landingPageContent,
        grossContractTotal,
        discountApplied,
        netContractTotal,
        jobRunning,
        builderCostBackend,
        calculateTaxValue,
    }

}

export default UtilServiceReact;